// importing predefined datepicker css styling
import 'react-datepicker/dist/react-datepicker.css';

import classNames from 'classnames';
import { forwardRef } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { MdDateRange } from 'react-icons/md';
import { useFieldState, WithFieldStateProps } from '../../hooks';
import { getCurrentI18nLanguage } from '../../i18n';

export interface DatepickerProps
  extends Omit<ReactDatePickerProps, 'placeholderText' | 'selectsRange'>,
    WithFieldStateProps {
  placeholder?: string;
  name?: string;
  size?: 'medium' | 'small';
  hideIcon?: boolean;
  selectsRange?: boolean;
}

/**
 * Date picker to pick and edit date
 */
export const Datepicker = forwardRef<ReactDatePicker, DatepickerProps>((props, ref) => {
  const {
    className,
    disabled,
    submitting,
    error,
    dirty,
    placeholder,
    name,
    readOnly,
    size = 'medium',
    hideIcon = false,
    isClearable,
    dateFormat,
    locale,
    ...otherProps
  } = props;

  const fieldState = useFieldState(props);

  const hasIcon = !hideIcon && !readOnly && !isClearable;
  const isEmpty =
    otherProps?.selected == null ||
    (typeof otherProps.selected === 'string' && (otherProps as any).selected.trim() === '');

  const { datepickerLocale, datepickerFormat } = getCurrentI18nLanguage();

  return (
    <div id="hell">
      <ReactDatePicker
        {...otherProps}
        // react-datepicker implements custom ref for its default input - therefor we need to set the ref of this `Datepicker` component
        // see for more information: https://github.com/orgs/react-hook-form/discussions/5413#discussioncomment-805331
        ref={(elem: any) => {
          elem && elem.input && ref && typeof ref === 'function' && ref(elem.input);
        }}
        showIcon
        icon={<MdDateRange className="text-brand-primary " />}
        locale={locale || datepickerLocale}
        dateFormat={dateFormat || datepickerFormat}
        name={name}
        disabled={disabled || submitting}
        readOnly={readOnly}
        isClearable={isClearable}
        placeholderText={placeholder}
        showPopperArrow={true}
        wrapperClassName="w-full"
        className={classNames(
          size === 'medium'
            ? classNames(
                'bg-[right_0.5rem_center] sm:text-sm bg-[length:1.5em_1.5em]',
                hasIcon ? 'pl-4 pr-10 py-2' : 'px-3 py-2', // padding for the bg icon
              )
            : undefined,
          size === 'small'
            ? classNames(
                'bg-[right_0.25rem_center] text-xs leading-[normal] bg-[length:1em_1em]',
                hasIcon ? 'pl-2 pr-6 py-1' : 'px-2 py-1', // padding for the bg icon
              )
            : undefined,
          'bg-no-repeat', // bg icon
          'appearance-none bg-surface-elevated dark:bg-neutral-700 border focus:outline-none focus:ring-offset-0 focus:ring-1 border-none text-white', // prestyling
          'block w-full rounded-md shadow-sm',
          fieldState === 'default' &&
            'border-gray-300 focus:ring-brand-primary focus:border-brand-primary dark:border-neutral-800 focus:dark:ring-cyan-500 focus:dark:border-cyan-500',
          fieldState === 'dirty' &&
            'border-brand-primary focus:ring-brand-primary focus:border-brand-primary dark:border-sky-300 focus:dark:ring-cyan-500 focus:dark:border-cyan-500',
          fieldState === 'error' &&
            'border-red-500 focus:ring-red-500 focus:border-red-500 placeholder-red-300 text-red-800 dark:border-red-400 focus:dark:ring-red-400 focus:dark:border-red-400',
          fieldState === 'readOnly' &&
            'read-only:bg-neutral-50 read-only:border-neutral-50 read-only:placeholder-inherit read-only:focus:ring-0 read-only:cursor-default read-only:dark:bg-neutral-900 read-only:dark:border-neutral-900',
          fieldState === 'disabled' &&
            'disabled:border-gray-200 disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:cursor-not-allowed disabled:dark:border-neutral-600',
          fieldState === 'disabledError' &&
            'disabled:border-red-300 disabled:focus:ring-0 disabled:placeholder-red-200 disabled:text-red-400 disabled:cursor-not-allowed disabled:dark:border-red-200',
          fieldState === 'disabledDirty' &&
            'disabled:border-cyan-600/70 disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:cursor-not-allowed disabled:dark:border-sky-200',
          fieldState === 'submitting' &&
            'disabled:border-gray-200 disabled:animate-pulse disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:dark:border-neutral-600',
          fieldState === 'submittingDirty' &&
            'disabled:border-cyan-600/70 disabled:animate-pulse disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:dark:border-sky-200',
          className,
        )}
        clearButtonClassName={classNames(isEmpty && 'opacity-0 invisible')}
      />
    </div>
  );
});

export default Datepicker;
